import { useCallback, useEffect, useState } from "react";

import { notification, TablePaginationConfig } from "antd";

import { FilterValue, SorterResult } from "antd/es/table/interface";

import _ from "lodash";

import type { EditableAccountAttributes } from "@/v1/types/IAccount.type";
import api from "../../../../../api";
import { useDeleteUserDependentMutation } from "../../../../../api/graphql/mutations/userDependent/deleteUserDependent.generated";
import {
  IUser,
  IUserDetails,
  KontaxUserStatus,
  KontaxUserStatusAdditionalParams,
  TableParams,
  UserDependent,
  UserRow,
} from "../../../../../types";
import {
  destroyMessage,
  showErrorMessage,
  showLoadingMessage,
  showSuccessMessage,
} from "../../../../../utils";
import EmptyWrapper from "../../../../common/EmptyWrapper";
import { UserFilterOptions } from "../../Common/UserFilters";
import { notifySavedChange } from "../../Common/utils";
import UsersTable from "./UsersTable";

const USER_UPDATING_KEY = "user-updating";
const DELETING_DEPENDENT_KEY = "deleting-dependent";

const UserList = ({
  filterOptions,
  setEmailParam,
  tableParams,
  setTableParams,
  setFilterOptions,
}: {
  filterOptions: UserFilterOptions;
  setEmailParam: (email: string) => void;
  tableParams: TableParams;
  setTableParams: (tableParams: TableParams) => void;
  setFilterOptions: (filterOptions: UserFilterOptions) => void;
}) => {
  const [users, setUsers] = useState<Array<IUser> | null>(null);
  const [deleteUserDependentHandler] = useDeleteUserDependentMutation();

  const tableParamsChanged = JSON.stringify(
    _.omit(tableParams, "pagination.total")
  );

  const fetchUsers = useCallback(
    async (skipSuccessMessage: boolean = false) => {
      const loadingKey = "loading-users";

      try {
        showLoadingMessage(loadingKey);

        const { users, count } = await api.kontax.getUsers({
          search: filterOptions.search,
          page: tableParams.pagination?.current,
          pageSize: tableParams.pagination?.pageSize,
          filters: _.omitBy(tableParams.filters, _.isNull),
          sortField: Array.isArray(tableParams.field)
            ? tableParams.field.join(".")
            : (tableParams.field as string),
          sortOrder: tableParams.order as string,
        });

        setUsers(users);
        setTableParams({
          ...tableParams,
          pagination: {
            ...tableParams.pagination,
            total: count,
          },
        });
        !skipSuccessMessage && showSuccessMessage();
      } catch (error) {
        showErrorMessage("Error fetching user");
        // eslint-disable-next-line no-console
        console.error("Error fetching user", error);
        setUsers([]);
      } finally {
        destroyMessage(loadingKey);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterOptions.search, setTableParams, tableParamsChanged]
  );

  useEffect(() => {
    if (filterOptions.search || filterOptions.all) {
      fetchUsers();
    } else {
      setUsers(null);
    }
  }, [fetchUsers, filterOptions]);

  const updateUserListWithChangedUser = useCallback(
    (updatedUser: IUser) => {
      const updatedUserList = users!.map((user) => {
        if (user.publicId === updatedUser.publicId) {
          return updatedUser;
        }

        return user;
      });
      setUsers(updatedUserList);
    },
    [users]
  );

  const updateUserStatus = useCallback(
    async (
      email: string,
      status: KontaxUserStatus | null,
      additionalParams?: KontaxUserStatusAdditionalParams
    ) => {
      try {
        showLoadingMessage(USER_UPDATING_KEY);
        const updatedUser = await api.kontax.updateUserStatus(
          email,
          status,
          additionalParams
        );
        updateUserListWithChangedUser(updatedUser);
        notifySavedChange();
      } catch (error) {
        throw error;
      } finally {
        destroyMessage(USER_UPDATING_KEY);
      }
    },
    [updateUserListWithChangedUser]
  );

  const editUser = useCallback(
    async (email: string, userDetails: IUserDetails) => {
      showLoadingMessage(USER_UPDATING_KEY);
      try {
        const updatedUser = await api.kontax.updateUser({
          email,
          ...userDetails,
        });
        updateUserListWithChangedUser(updatedUser);
        notifySavedChange();
      } catch (error) {
        throw error;
      } finally {
        destroyMessage(USER_UPDATING_KEY);
      }
    },
    [updateUserListWithChangedUser]
  );

  const editAccount = useCallback(
    async (id: number, payload: EditableAccountAttributes) => {
      showLoadingMessage(USER_UPDATING_KEY);
      try {
        const updatedAccount = await api.kontax.updateAccount(id, payload);
        const updatedUser = users!.find((user) => user.accountId === id);
        if (updatedUser) {
          updateUserListWithChangedUser({
            ...updatedUser,
            accountIban: updatedAccount.iban ?? "",
          });
        }
        notifySavedChange();
      } catch (error) {
        throw error;
      } finally {
        destroyMessage(USER_UPDATING_KEY);
      }
    },
    [updateUserListWithChangedUser, users]
  );

  const updateDependents = useCallback(
    async (email: string, userDependents: UserDependent[]) => {
      try {
        showLoadingMessage(USER_UPDATING_KEY);
        const updatedDependents = await api.kontax.updateDependents(
          email,
          userDependents
        );
        const updatedUserList = users!.map((user) => {
          if (user.email === email) {
            return {
              ...user,
              dependents: updatedDependents,
            };
          }

          return user;
        });

        notifySavedChange();
        setUsers(updatedUserList);
      } catch (error) {
        throw error;
      } finally {
        destroyMessage(USER_UPDATING_KEY);
      }
    },
    [users]
  );

  const deleteDependent = useCallback(
    async (email: string, id: string) => {
      showLoadingMessage(DELETING_DEPENDENT_KEY);
      try {
        const { data } = await deleteUserDependentHandler({
          variables: { id },
        });

        if (data?.deleteUserDependent.success) {
          const updatedUserList = users!.map((user) => {
            if (user.email === email) {
              return {
                ...user,
                dependents: user.dependents.filter(
                  (dependent) => dependent.id !== id
                ),
              };
            }

            return user;
          });
          setUsers(updatedUserList);
          showSuccessMessage("Dependent deleted successfully");
        }
      } catch (error) {
        notification.error({ message: `Error while deleting dependent` });
      } finally {
        destroyMessage(DELETING_DEPENDENT_KEY);
      }
    },
    [deleteUserDependentHandler, users]
  );

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<UserRow> | SorterResult<UserRow>[]
  ) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setUsers([]);
    }
  };

  return (
    <>
      {!!users?.length ? (
        <div style={{ marginTop: "12px" }}>
          <UsersTable
            users={users}
            tableParams={tableParams}
            onChange={handleTableChange}
            updateUserStatus={updateUserStatus}
            updateDependents={updateDependents}
            deleteDependent={deleteDependent}
            editUser={editUser}
            editAccount={editAccount}
            setEmailParam={setEmailParam}
            setFilterOptions={setFilterOptions}
          />
        </div>
      ) : (
        users && <EmptyWrapper description="No users found" />
      )}
    </>
  );
};

export default UserList;
